import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Paper,
  Snackbar,
  Alert,
  Typography,
  IconButton,
  CircularProgress,
  Fade,
  Grow,
} from '@mui/material';
import {
  User,
  X,
  MessageSquarePlus,
  ThumbsUp,
  ThumbsDown,
  ArrowUp,
} from 'lucide-mui';
import { posthog } from 'posthog-js';
import { RootState } from '../../../reducers';
import {
  sendChatMessageWithActionsStreaming,
  handleButtonAction,
  getChatHistory,
  getChatSession,
  sendChatFeedback,
  parseActionButtons,
  cleanResponseText,
} from '../../../services/chatService';
import type {
  ChatMessage,
  ChatContext,
  ActionButton,
} from '../../../services/chatService';
import {
  selectIsPreloaded,
  selectPreloadedData,
  clearPreloadedData,
} from './chatPreloadSlice';
import DynamicActionButton from './DynamicActionButton';
import botIcon from '../../../assets/icons/new_logo.svg';

interface AIAssistantFormProps {
  formSource?: string;
  onClose?: () => void;
}

// Overview component similar to Gemini chatbot - memoized to prevent re-renders
const Overview = React.memo(() => (
  <Fade in timeout={800}>
    <Box
      sx={{
        maxWidth: '100%',
        // mt: 4,
        // mb: 2,
        // mx: 'auto',
        // px: 2,
        textAlign: 'center',
      }}
    >
      <Typography
        variant="h5"
        fontWeight={600}
        color="text.secondary"
        sx={{
          lineHeight: 1.33,
          mt: 4,
        }}
      >
        Choose an option below or how can I help you?
      </Typography>
    </Box>
  </Fade>
));

const AIAssistantForm: React.FC<AIAssistantFormProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.settings);
  const { subscription, usage } = useSelector((state: RootState) => state.plan);
  const { selectedTeam } = useSelector((state: RootState) => state.team);
  const isPreloaded = useSelector(selectIsPreloaded);
  const preloadedData = useSelector(selectPreloadedData);

  // Enhanced quick action chips with more engaging design
  const quickActionChips = [
    {
      title: 'Help with recording',
      label: 'Trouble with recording or uploading videos.',
      icon: '🎥',
      color: '#F59E0B',
      predefinedQuestion:
        "I'm having trouble recording or uploading a video. Can you help me diagnose the problem?",
    },
    {
      title: 'Improve my transcript',
      label: 'Fix errors or language in your transcripts.',
      icon: '🎯',
      color: '#3B82F6',
      predefinedQuestion:
        "I'm having issues with my transcript. It might be inaccurate, incomplete, or in the wrong language. Can you help me improve it?",
    },
    {
      title: 'Account and Billing',
      label: 'Manage your subscription, usage, or billing.',
      icon: '💳',
      color: '#10B981',
      predefinedQuestion:
        'I need help with my account or billing. I might want to change my plan, check my usage, or ask about a payment.',
    },
    {
      title: 'Discover AI Features',
      label: 'Learn about AI summaries, translation & more.',
      icon: '🤖',
      color: '#8B5CF6',
      predefinedQuestion:
        "I'm interested in the AI features. What can I do with them and how do I get the best results from summaries or other tools?",
    },
    {
      title: 'Talk to sales',
      label: 'Contact with our sales team for pricing',
      icon: '💼',
      color: '#F97316',
      predefinedQuestion:
        'I would like to speak with someone from your sales team about pricing and enterprise options.',
    },
    {
      title: 'Mobile and desktop app',
      label: 'Tell me more about our mobile and desktop app.',
      icon: '📱',
      color: '#06B6D4',
      predefinedQuestion:
        'Can you tell me more about the ScreenApp mobile and desktop applications? How do I download and use them?',
    },
  ];

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isActionButtonLoading, setIsActionButtonLoading] = useState<
    string | null
  >(null);
  const [chatError, setChatError] = useState<string | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [messageFeedback, setMessageFeedback] = useState<
    Record<string, { type: 'like' | 'dislike'; loading: boolean }>
  >({});
  const [showScrollShadow, setShowScrollShadow] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Check if messages container has overflow and update shadow state
  const checkScrollOverflow = () => {
    const container = messagesContainerRef.current;
    if (container) {
      const hasOverflow = container.scrollHeight > container.clientHeight;
      setShowScrollShadow(hasOverflow);
    }
  };

  // Generate unique ID for messages
  const generateMessageId = () => {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Process messages loaded from history to handle action buttons
  const processHistoryMessages = (messages: ChatMessage[]): ChatMessage[] => {
    return messages.map((message) => {
      if (message.role === 'assistant') {
        // If the message doesn't have action buttons but has button text in content
        if (!message.actionButtons || message.actionButtons.length === 0) {
          const buttons = parseActionButtons(message.content);
          if (buttons.length > 0) {
            return {
              ...message,
              actionButtons: buttons,
              content: cleanResponseText(message.content),
              id: message.id || generateMessageId(),
            };
          }
        } else {
          // Clean any remaining button text from content if action buttons exist
          return {
            ...message,
            content: cleanResponseText(message.content),
            id: message.id || generateMessageId(),
          };
        }
      }

      return {
        ...message,
        id: message.id || generateMessageId(),
      };
    });
  };

  // Load previous chat on mount - use preloaded data if available
  useEffect(() => {
    const loadPreviousChat = async () => {
      // First, check if we have preloaded data
      if (isPreloaded && preloadedData.messages.length > 0) {
        setChatMessages(preloadedData.messages);
        setCurrentSessionId(preloadedData.sessionId);
        // Clear preloaded data after using it to free memory
        dispatch(clearPreloadedData());
        return;
      }

      // Fallback to loading from API if no preloaded data
      try {
        const historyResponse = await getChatHistory(1, 1);
        if (
          historyResponse.success &&
          historyResponse.data &&
          historyResponse.data.sessions &&
          historyResponse.data.sessions.length > 0
        ) {
          const lastSession = historyResponse.data.sessions[0];

          // Skip escalated sessions - don't load them
          if (lastSession.status === 'escalated') {
            console.log(
              'Last session was escalated, starting fresh chat instead'
            );
            return;
          }

          const sessionResponse = await getChatSession(lastSession.sessionId);

          if (
            sessionResponse.success &&
            sessionResponse.data &&
            sessionResponse.data.messages &&
            sessionResponse.data.messages.length > 0 &&
            sessionResponse.data.status !== 'escalated' // Double-check escalation status
          ) {
            const processedMessages = processHistoryMessages(
              sessionResponse.data.messages
            );
            setChatMessages(processedMessages);
            setCurrentSessionId(sessionResponse.data.sessionId);
          }
        }
      } catch (error) {
        console.log('No previous chat history found or error loading:', error);
        // Silently fail - user will start with fresh chat
      }
    };

    loadPreviousChat();
  }, [isPreloaded, preloadedData, dispatch]);

  const handleNewChat = () => {
    setChatMessages([]);
    setCurrentSessionId(null);
    setChatInput('');
    setChatError(null);
  };

  // Helper function to format message content consistently
  const formatMessageContent = (content: string) => {
    let result = content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>');

    // Handle mixed bullet/numbered lists by splitting on numbered items within text
    result = result
      // Split numbered items that appear mid-sentence (like "navigation). 2. Go to")
      .replace(/\)\.\s+(\d+\.\s+)/g, ').</p><p>$1')
      // Split numbered items that appear after periods (like "accuracy. 5. Select")
      .replace(/\.\s+(\d+\.\s+)/g, '.</p><p>$1')
      // Convert bullet points to list items
      .replace(/^[\s]*[-•]\s+(.+?)(?=\s+\d+\.|$)/gm, '<li>$1</li>')
      // Convert numbered items to list items
      .replace(/^[\s]*(\d+\.\s+)(.+?)(?=\s+\d+\.|$)/gm, '<li>$2</li>')
      // Convert remaining paragraphs
      .replace(/^(?!<li>|<\/li>|<ul>|<\/ul>)(.+)$/gm, '<p>$1</p>');

    // Wrap consecutive list items in ul tags
    result = result.replace(
      /(<li>.*?<\/li>(?:\s*<li>.*?<\/li>)*)/g,
      '<ul>$1</ul>'
    );

    // Clean up excessive whitespace and line breaks
    result = result.replace(/\s*\n\s*/g, '');

    // Special formatting for transcript content - support both new and legacy formats
    if (
      content.includes('📹 **Video:') ||
      content.includes('📄 **Transcript Preview:**') ||
      content.includes('## 📝 Transcript Preview')
    ) {
      result = result
        // Format video title with proper spacing
        .replace(
          /📹 \*\*Video:\s*(.*?)\*\*/g,
          '<div style="font-size: 18px; font-weight: 600; color: #1976d2; margin-bottom: 16px; display: flex; align-items: center;"><span style="margin-right: 8px;">📹</span>Video: $1</div>'
        )
        // New format: ## 📝 Transcript Preview header with card styling
        .replace(
          /## 📝 Transcript Preview/g,
          '<div style="font-size: 16px; font-weight: 600; color: #1976d2; margin: 20px 0 16px 0; padding: 12px 16px; background: rgba(25, 118, 210, 0.05); border-left: 4px solid #1976d2; border-radius: 8px; display: flex; align-items: center;"><span style="margin-right: 8px;">📝</span>Transcript Preview</div>'
        )
        // Format transcript preview header with spacing
        .replace(
          /📄 \*\*Transcript Preview:\*\*/g,
          '<div style="font-size: 16px; font-weight: 600; color: #424242; margin: 20px 0 12px 0; display: flex; align-items: center;"><span style="margin-right: 8px;">📄</span>Transcript Preview:</div>'
        )
        // Format timestamp entries with enhanced styling (supports MM:SS and HH:MM:SS)
        .replace(
          /\[(\d{1,2}:\d{2}(?::\d{2})?)\]\s*(.*?)(?=\[|\n|$)/g,
          '<div style="margin-bottom: 16px; padding: 12px 16px; background-color: #f8f9fa; border-radius: 8px; border-left: 3px solid #1976d2;"><div style="font-size: 12px; color: #1976d2; font-weight: 500; margin-bottom: 6px; font-family: monospace; background: rgba(25, 118, 210, 0.1); padding: 2px 6px; border-radius: 4px; display: inline-block;">[$1]</div><div style="line-height: 1.6; color: #333;">$2</div></div>'
        )
        // Format Read more link with styling
        .replace(
          /\[Read more\.\.\.\]/g,
          '<div style="margin: 20px 0; padding: 12px 16px; background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%); border-radius: 8px; border: 1px solid #e0e0e0;"><div style="font-weight: 600; color: #1976d2; margin-bottom: 4px;">📖 Read More</div><div style="font-size: 14px; color: #666;">Full transcript available in your video library</div></div>'
        )
        // Handle ellipsis for truncated content
        .replace(
          /(\.{3}\s*$)/,
          '<span style="color: #666; font-style: italic; font-size: 0.9em;">... <em>Read more in full video analysis</em></span>'
        )
        // Handle remaining line breaks for non-transcript content
        .replace(/\n{3,}/g, '\n\n')
        .replace(/\n\n/g, '<div style="margin-bottom: 12px;"></div>')
        .replace(/\n/g, '<br />');
    } else {
      // Standard formatting for non-transcript content
      result = result
        // STEP 1: Fix numbered steps running together - comprehensive approach
        // Pattern: "1. **Title:** text 2. **Next Title:** more text"
        .replace(
          /(\d+\.\s+\*\*[^*]+\*\*:?\s*[^0-9]*?)(\s*)(\d+\.\s+\*\*)/g,
          '$1\n\n$3'
        )

        // STEP 2: Handle edge cases with buttons or end of message
        .replace(
          /(\d+\.\s+\*\*[^*]+\*\*:?\s*[^0-9]*?)(\s*\[BUTTON|\s*$)/g,
          '$1\n\n$2'
        )

        // STEP 3: Clean up excessive line breaks (3+ becomes 2)
        .replace(/\n{3,}/g, '\n\n')

        // STEP 4: Convert numbered lists to properly formatted HTML with improved spacing
        .replace(/(\d+)\.\s+\*\*([^*]+)\*\*:?\s*/g, (match, num, title) => {
          return `<div style="margin: 20px 0; padding: 16px; background: rgba(25, 118, 210, 0.02); border-left: 4px solid #1976d2; border-radius: 8px;">
            <div style="font-weight: 700; color: #1976d2; font-size: 16px; margin-bottom: 12px; display: flex; align-items: center;">
              <span style="background: #1976d2; color: white; width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 14px; margin-right: 12px;">${num}</span>
              <span>${title}</span>
            </div>
            <div style="line-height: 1.6; color: #333; margin-left: 40px;" class="step-content">`;
        })

        // STEP 5: Close the step content divs by detecting next step or end
        .replace(
          /(<div style="line-height: 1\.6; color: #333; margin-left: 40px;" class="step-content">)(.*?)(?=<div style="margin: 20px 0; padding: 16px|$)/g,
          (match, opening, stepContent) => {
            return `${opening}${stepContent.trim()}</div></div>`;
          }
        )

        // STEP 6: Handle content that comes after the last step
        .replace(
          /(<div style="line-height: 1\.6; color: #333; margin-left: 40px;" class="step-content">)(.*?)$/g,
          '$1$2</div></div>'
        )

        // STEP 7: Handle double line breaks for section spacing
        .replace(/\n\n/g, '<div style="margin-bottom: 16px;"></div>')

        // STEP 8: Handle single line breaks
        .replace(/\n/g, '<br />');
    }

    // Special handling for sections with emojis - add extra spacing before emoji headers
    result = result.replace(
      /(<div style="margin-bottom: 12px;"><\/div>)([\u{1F3AF}\u{1F527}\u{26A0}\u{2705}\u{1F4CB}\u{1F31F}\u{1F4A1}\u{1F680}\u{1F4CA}])/gu,
      '$1<div style="margin-bottom: 4px;"></div>$2'
    );

    // Debug test with sample content
    if (content.includes('📹') || content.includes('�')) {
      console.log('🔍 Transcript Formatting Test:', {
        original: content,
        formatted: result,
        hasVideoTitle: content.includes('📹 **Video:'),
        hasTranscriptPreview: content.includes('📄 **Transcript Preview:**'),
      });
    }

    return result;
  };

  useEffect(() => {
    scrollToBottom();
    // Check for overflow after messages update
    setTimeout(checkScrollOverflow, 100);
  }, [chatMessages]);

  // Check for overflow when messages container is rendered
  useEffect(() => {
    // Initial check after component mounts
    setTimeout(checkScrollOverflow, 100);
  }, [chatMessages.length]);

  // Helper function to parse user agent and extract browser details
  const parseBrowserInfo = () => {
    const {
      userAgent,
      platform,
      language,
      languages: navLanguages,
      appVersion,
      appName,
    } = navigator;
    const languages = navLanguages?.join(', ') || language;

    // Enhanced browser detection with comprehensive logic
    let browserName = 'Unknown';
    let browserVersion = 'Unknown';
    let fullVersion = String(parseFloat(appVersion));
    let majorVersion = parseInt(appVersion, 10);
    let nameOffset: number;
    let verOffset: number;
    let ix: number;

    // In Opera, the true version is after "OPR" or after "Version"
    verOffset = userAgent.indexOf('OPR');
    if (verOffset !== -1) {
      browserName = 'Opera';
      fullVersion = userAgent.substring(verOffset + 4);
      const versionOffset = userAgent.indexOf('Version');
      if (versionOffset !== -1) {
        fullVersion = userAgent.substring(versionOffset + 8);
      }
    }
    // In MS Edge, the true version is after "Edg" in userAgent
    else {
      verOffset = userAgent.indexOf('Edg');
      if (verOffset !== -1) {
        browserName = 'Microsoft Edge';
        fullVersion = userAgent.substring(verOffset + 4);
      }
      // In MSIE, the true version is after "MSIE" in userAgent
      else {
        verOffset = userAgent.indexOf('MSIE');
        if (verOffset !== -1) {
          browserName = 'Microsoft Internet Explorer';
          fullVersion = userAgent.substring(verOffset + 5);
        }
        // In Chrome, the true version is after "Chrome"
        else {
          verOffset = userAgent.indexOf('Chrome');
          if (verOffset !== -1) {
            browserName = 'Chrome';
            fullVersion = userAgent.substring(verOffset + 7);
          }
          // In Safari, the true version is after "Safari" or after "Version"
          else {
            verOffset = userAgent.indexOf('Safari');
            if (verOffset !== -1) {
              browserName = 'Safari';
              fullVersion = userAgent.substring(verOffset + 7);
              const versionOffset = userAgent.indexOf('Version');
              if (versionOffset !== -1) {
                fullVersion = userAgent.substring(versionOffset + 8);
              }
            }
            // In Firefox, the true version is after "Firefox"
            else {
              verOffset = userAgent.indexOf('Firefox');
              if (verOffset !== -1) {
                browserName = 'Firefox';
                fullVersion = userAgent.substring(verOffset + 8);
              }
              // In most other browsers, "name/version" is at the end of userAgent
              else {
                nameOffset = userAgent.lastIndexOf(' ') + 1;
                verOffset = userAgent.lastIndexOf('/');
                if (nameOffset < verOffset) {
                  browserName = userAgent.substring(nameOffset, verOffset);
                  fullVersion = userAgent.substring(verOffset + 1);
                  if (browserName.toLowerCase() === browserName.toUpperCase()) {
                    browserName = appName;
                  }
                }
              }
            }
          }
        }
      }
    }

    // Trim the fullVersion string at semicolon/space if present
    ix = fullVersion.indexOf(';');
    if (ix !== -1) {
      fullVersion = fullVersion.substring(0, ix);
    }
    ix = fullVersion.indexOf(' ');
    if (ix !== -1) {
      fullVersion = fullVersion.substring(0, ix);
    }

    majorVersion = parseInt(String(fullVersion), 10);
    if (Number.isNaN(majorVersion)) {
      fullVersion = String(parseFloat(appVersion));
      majorVersion = parseInt(appVersion, 10);
    }

    // Enhanced OS detection
    let osName = 'Unknown OS';
    if (appVersion.indexOf('Win') !== -1) osName = 'Windows';
    if (appVersion.indexOf('Mac') !== -1) osName = 'macOS';
    if (appVersion.indexOf('X11') !== -1) osName = 'UNIX';
    if (appVersion.indexOf('Linux') !== -1) osName = 'Linux';

    // More detailed OS version detection
    let osVersion = 'Unknown';
    if (osName === 'Windows') {
      if (appVersion.indexOf('Windows NT 10.0') !== -1) osVersion = '10/11';
      else if (appVersion.indexOf('Windows NT 6.3') !== -1) osVersion = '8.1';
      else if (appVersion.indexOf('Windows NT 6.2') !== -1) osVersion = '8';
      else if (appVersion.indexOf('Windows NT 6.1') !== -1) osVersion = '7';
      else if (appVersion.indexOf('Windows NT 6.0') !== -1) osVersion = 'Vista';
      else if (appVersion.indexOf('Windows NT 5.1') !== -1) osVersion = 'XP';
    } else if (osName === 'macOS') {
      const macMatch = userAgent.match(/Mac OS X (\d+[._]\d+[._]?\d*)/);
      if (macMatch) {
        osVersion = macMatch[1].replace(/_/g, '.');
      }
    }

    const versionParts = fullVersion.split('.');
    browserVersion = `${versionParts[0]}.${versionParts[1] || '0'}`;

    return {
      name: browserName,
      version: browserVersion,
      fullVersion,
      majorVersion,
      fullUserAgent: userAgent,
      language,
      languages,
      platform,
      operatingSystem: osName,
      osVersion,
      vendor: navigator.vendor || 'Unknown',
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      hardwareConcurrency: navigator.hardwareConcurrency || 'Unknown',
      memory: (navigator as any).deviceMemory || 'Unknown',
    };
  };

  // Helper function to detect device type
  const getDeviceInfo = () => {
    const { userAgent, platform, maxTouchPoints = 0 } = navigator;

    let deviceType = 'desktop';

    // Mobile detection
    if (
      /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)
    ) {
      deviceType = 'mobile';
    }
    // Tablet detection
    else if (
      /iPad|Android.*Tablet|Windows.*Touch/i.test(userAgent) ||
      maxTouchPoints > 2
    ) {
      deviceType = 'tablet';
    }
    // Desktop detection based on platform
    else if (/Win|Mac|Linux|X11/i.test(platform)) {
      deviceType = 'desktop';
    }

    return {
      type: deviceType,
      maxTouchPoints,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      pixelRatio: window.devicePixelRatio || 1,
    };
  };

  // Helper function to generate session information
  const getSessionInfo = () => {
    // Generate or get tab ID
    let tabId = sessionStorage.getItem('screenapp-tab-id');
    if (!tabId) {
      tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('screenapp-tab-id', tabId);
    }

    return {
      sessionStart:
        sessionStorage.getItem('screenapp-session-start') ||
        new Date().toISOString(),
      tabId,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString(),
      referrer: document.referrer || 'direct',
      origin: window.location.origin,
    };
  };

  // Helper function to collect relevant localStorage data (non-sensitive)
  const getRelevantLocalStorageData = () => {
    const relevantKeys = [
      'screenapp-theme',
      'screenapp-language',
      'screenapp-last-used-team',
      'screenapp-user-preferences',
      'screenapp-feature-flags',
    ];

    const data: Record<string, any> = {};
    relevantKeys.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value) {
        try {
          data[key] = JSON.parse(value);
        } catch {
          data[key] = value;
        }
      }
    });

    return data;
  };

  const getCurrentUrlContext = () => {
    const currentUrl = window.location.href;
    const urlParams = new URLSearchParams(window.location.search);
    const hashPath = window.location.hash.replace('#', '');
    const pathParts = hashPath.split('/').filter(Boolean);
    const regularPathParts = window.location.pathname
      .split('/')
      .filter(Boolean);

    const context: any = {
      currentPage: currentUrl,
      path: hashPath || window.location.pathname,
      hostname: window.location.hostname,
      protocol: window.location.protocol,
      port: window.location.port,
      referrer: document.referrer,
      title: document.title,
      hash: window.location.hash,
      search: window.location.search,
    };

    const allPathParts = pathParts.length > 0 ? pathParts : regularPathParts;

    if (allPathParts.includes('library') && allPathParts.length >= 4) {
      const libraryIndex = allPathParts.indexOf('library');
      if (allPathParts[libraryIndex + 1]) {
        context.teamId = allPathParts[libraryIndex + 1];
      }
      if (allPathParts[libraryIndex + 3]) {
        context.fileId = allPathParts[libraryIndex + 3];
      }
    }

    if (allPathParts.includes('files') && allPathParts.length > 1) {
      const fileIndex = allPathParts.indexOf('files');
      if (allPathParts[fileIndex + 1]) {
        context.fileId = allPathParts[fileIndex + 1];
      }
    }

    if (
      !context.teamId &&
      allPathParts.includes('team') &&
      allPathParts.length > 1
    ) {
      const teamIndex = allPathParts.indexOf('team');
      if (allPathParts[teamIndex + 1]) {
        context.teamId = allPathParts[teamIndex + 1];
      }
    }

    // Add section detection
    if (allPathParts.includes('dashboard')) {
      context.section = 'dashboard';
    } else if (allPathParts.includes('library')) {
      context.section = 'library';
    } else if (allPathParts.includes('teams')) {
      context.section = 'teams';
    } else if (allPathParts.includes('settings')) {
      context.section = 'settings';
    } else if (allPathParts.includes('billing')) {
      context.section = 'billing';
    }

    if (urlParams.toString()) {
      context.urlParams = Object.fromEntries(urlParams);
    }

    return context;
  };

  const buildChatContext = (): ChatContext => {
    const getPlanLimits = (packageType?: string) => {
      switch (packageType) {
        case 'FREE':
          return { uploads: 3, transcriptions: 0, aiQuestions: 0 };
        case 'FREE_LOGGEDIN':
          return { uploads: 3, transcriptions: 1, aiQuestions: 3 };
        case 'GROWTH':
          return { uploads: 50, transcriptions: 50, aiQuestions: 50 };
        case 'BUSINESS':
          return {
            uploads: 'unlimited',
            transcriptions: 'unlimited',
            aiQuestions: 999,
          };
        case 'ENTERPRISE':
          return {
            uploads: 'unlimited',
            transcriptions: 'unlimited',
            aiQuestions: 'unlimited',
          };
        default:
          return { uploads: 0, transcriptions: 0, aiQuestions: 0 };
      }
    };

    const planLimits = getPlanLimits(subscription?.package);
    const currentUsage = usage || {};

    // Collect comprehensive context information
    const browserInfo = parseBrowserInfo();
    const deviceInfo = getDeviceInfo();
    const sessionInfo = getSessionInfo();
    const localStorage = getRelevantLocalStorageData();
    const urlContext = getCurrentUrlContext();

    return {
      userName: user?.name,
      userEmail: user?.email,
      subscription: subscription
        ? {
            package: subscription.package,
            term: subscription.term,
            price: subscription.price,
            renewalDate: subscription.currentPeriodStartedAt
              ? new Date(
                  new Date(subscription.currentPeriodStartedAt).getTime() +
                    (subscription.term === 'annual'
                      ? 365 * 24 * 60 * 60 * 1000
                      : 30 * 24 * 60 * 60 * 1000)
                ).toLocaleDateString()
              : undefined,
            currentPeriodStartedAt: subscription.currentPeriodStartedAt,
          }
        : undefined,
      usage: usage
        ? {
            uploadCount: currentUsage.uploadCount || 0,
            uploadLimit: planLimits.uploads,
            uploadRemaining:
              typeof planLimits.uploads === 'number'
                ? Math.max(
                    0,
                    planLimits.uploads - (currentUsage.uploadCount || 0)
                  )
                : 'unlimited',
            askAICount: currentUsage.askAICount || 0,
            askAILimit: planLimits.aiQuestions,
            askAIRemaining:
              typeof planLimits.aiQuestions === 'number'
                ? Math.max(
                    0,
                    planLimits.aiQuestions - (currentUsage.askAICount || 0)
                  )
                : 'unlimited',
            transcriptionCount: currentUsage.transcriptionCount || 0,
            transcriptionLimit: planLimits.transcriptions,
            transcriptionRemaining:
              typeof planLimits.transcriptions === 'number'
                ? Math.max(
                    0,
                    planLimits.transcriptions -
                      (currentUsage.transcriptionCount || 0)
                  )
                : 'unlimited',
            videoAskAICount: currentUsage.videoAskAICount || 0,
          }
        : undefined,
      team: selectedTeam
        ? {
            name: selectedTeam.name,
            membersCount: selectedTeam.teamSpaceMembers?.length,
            teamId: selectedTeam.teamId,
            ownerEmail: selectedTeam.email,
            role: selectedTeam.email === user?.email ? 'Owner' : 'Member',
          }
        : undefined,
      urlContext,
      browserInfo,
      deviceInfo,
      sessionInfo,
      localStorage,
    };
  };

  const handleChatSend = async (message?: string) => {
    const messageToSend = message || chatInput.trim();
    if (!messageToSend || isStreaming) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: messageToSend,
      timestamp: new Date(),
      id: generateMessageId(),
    };

    setChatMessages((prev) => [...prev, userMessage]);
    setChatInput('');
    setIsStreaming(true);
    setChatError(null);

    // Add a placeholder message for streaming
    const streamingMessage: ChatMessage = {
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      id: generateMessageId(),
    };
    setChatMessages((prev) => [...prev, streamingMessage]);

    try {
      const context = buildChatContext();
      const conversationHistory = chatMessages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      const response = await sendChatMessageWithActionsStreaming(
        messageToSend,
        context,
        conversationHistory,
        currentSessionId || undefined,
        (token: string) => {
          // Update the streaming message in real-time
          setChatMessages((prev) =>
            prev.map((msg, index) =>
              index === prev.length - 1
                ? { ...msg, content: msg.content + token }
                : msg
            )
          );
        }
      );

      if (response.sessionId && !currentSessionId) {
        setCurrentSessionId(response.sessionId);
      }

      // Update the final message with complete data (preserve streamed content)
      setChatMessages((prev) =>
        prev.map((msg, index) =>
          index === prev.length - 1
            ? {
                ...msg,
                // Keep the streamed content, don't replace it
                toolCalls: response.toolCalls,
                actionButtons: response.actionButtons || [],
                escalated: response.escalated,
              }
            : msg
        )
      );
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'An unexpected error occurred';
      setChatError(errorMessage);

      // Check if this is a system error that should trigger automatic escalation
      const isSystemError =
        errorMessage.includes('validation failed') ||
        errorMessage.includes('Cannot populate path') ||
        errorMessage.includes('JSON input') ||
        errorMessage.includes('Path `content` is required') ||
        errorMessage.includes('StrictPopulateError') ||
        errorMessage.includes('Unexpected end of JSON input') ||
        errorMessage.includes('escalateImmediately');

      let errorContent =
        '🚨 I encountered a technical issue. Our support team has been automatically notified and will contact you directly at your email address within 24 hours to resolve this issue.\n\n⚠️ Please check your email inbox (and spam folder) for our response.\n\n📧 You can also contact us directly at support@screenapp.io if you need immediate assistance.';

      if (isSystemError) {
        errorContent =
          "🚨 I encountered a technical issue on our end. I've automatically escalated this to our support team with all the technical details.\n\n📧 **Our support team will contact you directly at your email address within 24 hours** to resolve this issue.\n\n⚠️ Please check your email inbox (and spam folder) for our response.\n\n📧 You can also contact us directly at support@screenapp.io if you need immediate assistance.";

        // Set snackbar to inform about automatic escalation
        setSnackbar({
          open: true,
          message:
            'System error detected - support team automatically notified',
          severity: 'info',
        });
      }

      // Replace the streaming message with error message
      setChatMessages((prev) =>
        prev.map((msg, index) =>
          index === prev.length - 1
            ? {
                ...msg,
                content: errorContent,
              }
            : msg
        )
      );
    } finally {
      setIsStreaming(false);
    }
  };

  const handleQuickAction = async (chipData: any) => {
    try {
      // Send PostHog event for quick action click
      posthog.capture('support-quick-action-clicked', {
        action_title: chipData.title,
        action_label: chipData.label,
        action_icon: chipData.icon,
        action_color: chipData.color,
        predefined_question: chipData.predefinedQuestion,
      });

      setIsStreaming(true);
      setChatError(null);

      const userMessage: ChatMessage = {
        role: 'user',
        content: chipData.title,
        timestamp: new Date(),
        id: generateMessageId(),
      };
      setChatMessages((prev) => [...prev, userMessage]);

      // Use multi-agent system for all quick actions
      const streamingMessage: ChatMessage = {
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        id: generateMessageId(),
      };
      setChatMessages((prev) => [...prev, streamingMessage]);

      const context = buildChatContext();
      const conversationHistory = chatMessages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      // Force specific agent based on quick action
      let forceAgent: string;
      switch (chipData.title) {
        case 'Help with recording':
          forceAgent = 'technical';
          break;
        case 'Improve my transcript':
          forceAgent = 'technical';
          break;
        case 'Account and Billing':
        case 'Cancel Subscription':
        case 'Accidental Charge':
        case 'Monthly Plan Options':
        case 'Check Usage & Limits':
          forceAgent = 'billing';
          break;
        case 'Discover AI Features':
          forceAgent = 'ai-features';
          break;
        default:
          forceAgent = 'general';
      }

      const response = await sendChatMessageWithActionsStreaming(
        chipData.predefinedQuestion,
        context,
        conversationHistory,
        currentSessionId || undefined,
        (token: string) => {
          // Update the streaming message in real-time
          setChatMessages((prev) =>
            prev.map((msg, index) =>
              index === prev.length - 1
                ? { ...msg, content: msg.content + token }
                : msg
            )
          );
        },
        { useMultiAgent: true, forceAgent }
      );

      if (response.sessionId && !currentSessionId) {
        setCurrentSessionId(response.sessionId);
      }

      // Update the final message with complete data (preserve streamed content)
      setChatMessages((prev) =>
        prev.map((msg, index) =>
          index === prev.length - 1
            ? {
                ...msg,
                // Keep the streamed content, don't replace it
                toolCalls: response.toolCalls,
                actionButtons: response.actionButtons || [],
                escalated: response.escalated,
              }
            : msg
        )
      );
    } catch (error) {
      console.error('Error handling quick action:', error);
      setChatError('Failed to load assistance. Please try again.');
    } finally {
      setIsStreaming(false);
    }
  };

  const handleChatKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChatSend();
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Handle feedback submission
  const handleFeedback = async (
    messageId: string,
    feedbackType: 'like' | 'dislike'
  ) => {
    if (!currentSessionId) {
      setSnackbar({
        open: true,
        message: 'Cannot submit feedback without an active session.',
        severity: 'error',
      });
      return;
    }

    // Check if the same feedback is already selected - if so, remove it
    const currentFeedback =
      messageFeedback[messageId]?.type ||
      chatMessages.find(
        (msg) =>
          msg.id === messageId ||
          (messageId === 'test-id' && msg.role === 'assistant')
      )?.feedback?.type;

    if (currentFeedback === feedbackType) {
      // Remove feedback
      setMessageFeedback((prev) => {
        const newState = { ...prev };
        delete newState[messageId];
        return newState;
      });

      // Remove feedback from message
      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ||
          (messageId === 'test-id' && msg.role === 'assistant')
            ? {
                ...msg,
                feedback: undefined,
              }
            : msg
        )
      );
      return;
    }

    // Immediately update visual state for instant feedback
    setMessageFeedback((prev) => ({
      ...prev,
      [messageId]: { type: feedbackType, loading: false },
    }));

    // Update the message in chatMessages to include feedback immediately
    setChatMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId ||
        (messageId === 'test-id' && msg.role === 'assistant')
          ? {
              ...msg,
              feedback: {
                type: feedbackType,
                timestamp: new Date(),
              },
            }
          : msg
      )
    );

    try {
      const result = await sendChatFeedback(
        messageId,
        currentSessionId,
        feedbackType
      );

      if (!result.success) {
        // Only show error messages, remove visual feedback on failure
        setMessageFeedback((prev) => {
          const newState = { ...prev };
          delete newState[messageId];
          return newState;
        });

        // Remove feedback from message on failure
        setChatMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId ||
            (messageId === 'test-id' && msg.role === 'assistant')
              ? {
                  ...msg,
                  feedback: undefined,
                }
              : msg
          )
        );

        setSnackbar({
          open: true,
          message: result.message || 'Failed to submit feedback.',
          severity: 'error',
        });
      }
      // No success message - just keep the visual feedback
    } catch {
      // Remove visual feedback on error
      setMessageFeedback((prev) => {
        const newState = { ...prev };
        delete newState[messageId];
        return newState;
      });

      // Remove feedback from message on error
      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ||
          (messageId === 'test-id' && msg.role === 'assistant')
            ? {
                ...msg,
                feedback: undefined,
              }
            : msg
        )
      );

      setSnackbar({
        open: true,
        message: 'Failed to submit feedback. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleActionButtonClick = async (button: ActionButton) => {
    try {
      setIsActionButtonLoading(button.id);
      const context = buildChatContext();

      const userActionMessage: ChatMessage = {
        role: 'user',
        content: `${button.label}`,
        timestamp: new Date(),
        id: generateMessageId(),
      };
      setChatMessages((prev) => [...prev, userActionMessage]);

      const result = await handleButtonAction(button, context);

      if (result.success) {
        if (result.data?.logout) {
          // Handle logout for cache clearing
          const { delay = 3000 } = result.data;

          setSnackbar({
            open: true,
            message:
              result.message ||
              'Cache cleared successfully. Refreshing page...',
            severity: 'info',
          });

          const assistantMessage: ChatMessage = {
            role: 'assistant',
            content: result.message || 'Cache cleared successfully.',
            timestamp: new Date(),
            id: generateMessageId(),
          };
          setChatMessages((prev) => [...prev, assistantMessage]);

          // Logout and refresh page after delay
          setTimeout(() => {
            // Clear any remaining auth tokens
            localStorage.removeItem('token');
            sessionStorage.removeItem('token');
            // Refresh the page to complete the cache clearing process
            window.location.reload();
          }, delay);
        } else if (result.data?.redirect) {
          const { redirect, openInNewTab } = result.data;
          if (openInNewTab) {
            window.open(redirect, '_blank');
          } else {
            navigate(redirect);
          }
          setSnackbar({
            open: true,
            message: result.message || 'Redirecting...',
            severity: 'success',
          });
        } else {
          setSnackbar({
            open: true,
            message: result.message || 'Action completed successfully.',
            severity: 'success',
          });
        }

        // Only add assistant message if not logging out (to avoid duplicate messages)
        if (!result.data?.logout) {
          const assistantMessage: ChatMessage = {
            role: 'assistant',
            content: result.message || 'Action completed successfully.',
            timestamp: new Date(),
            id: generateMessageId(),
          };
          setChatMessages((prev) => [...prev, assistantMessage]);
        }
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Action failed.',
          severity: 'error',
        });

        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: result.message || 'Action failed. Please try again.',
          timestamp: new Date(),
          id: generateMessageId(),
        };
        setChatMessages((prev) => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error('Error handling button action:', error);

      setSnackbar({
        open: true,
        message: 'An error occurred while processing the action.',
        severity: 'error',
      });

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content:
          'An error occurred while processing the action. Please try again.',
        timestamp: new Date(),
        id: generateMessageId(),
      };
      setChatMessages((prev) => [...prev, assistantMessage]);
    } finally {
      setIsActionButtonLoading(null);
    }
  };

  // Debug function to test enhanced browser detection
  const debugBrowserInfo = () => {
    const browserInfo = parseBrowserInfo();
    const deviceInfo = getDeviceInfo();
    const sessionInfo = getSessionInfo();

    console.log('🔍 Enhanced Browser Detection Debug:', {
      browserInfo,
      deviceInfo,
      sessionInfo,
      fullContext: buildChatContext(),
    });

    // Display in a more readable format
    const debugInfo = `
🌐 Browser: ${browserInfo.name} ${browserInfo.version} (${
      browserInfo.fullVersion
    })
💻 OS: ${browserInfo.operatingSystem} ${browserInfo.osVersion}
📱 Platform: ${browserInfo.platform}
🖥️ Device: ${deviceInfo.type}
📐 Screen: ${deviceInfo.screenWidth}x${deviceInfo.screenHeight}
🔢 CPU Cores: ${browserInfo.hardwareConcurrency}
💾 RAM: ${browserInfo.memory}GB
🌍 Language: ${browserInfo.language}
🕐 Timezone: ${sessionInfo.timeZone}
🍪 Cookies: ${browserInfo.cookieEnabled ? 'Enabled' : 'Disabled'}
📶 Online: ${browserInfo.onLine ? 'Yes' : 'No'}
    `;

    console.log('📊 Formatted Debug Info:', debugInfo);
    return debugInfo;
  };

  // Expose debug function to window for easy testing
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).debugScreenAppBrowser = debugBrowserInfo;
      console.log(
        '🚀 Debug function available: call window.debugScreenAppBrowser() in console'
      );
    }
  }, []);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        bgcolor: (theme) => theme.palette.background.paper,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Action buttons - positioned absolutely */}
      <Box
        sx={{
          position: 'absolute',
          top: 18,
          right: 9,
          zIndex: 10,
          display: 'flex',
          gap: 1,
        }}
      >
        {/* New chat button - only show if there are messages */}
        {chatMessages.length > 0 && (
          <IconButton
            onClick={handleNewChat}
            sx={{
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.05)',
              backdropFilter: 'blur(8px)',
              '&:hover': {
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.1)'
                    : 'rgba(0,0,0,0.1)',
              },
            }}
          >
            <MessageSquarePlus sx={{ width: 20, height: 20 }} />
          </IconButton>
        )}

        {/* Close button */}
        {onClose && (
          <IconButton
            onClick={onClose}
            sx={{
              backdropFilter: 'blur(8px)',
              '&:hover': {
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.1)'
                    : 'rgba(0,0,0,0.1)',
              },
            }}
          >
            <X sx={{ width: 20, height: 20 }} />
          </IconButton>
        )}
      </Box>

      {/* Main chat container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          width: '100%',
          height: '100%',
          // maxWidth: 800,
          mx: 'auto',
          py: '72px',
          minHeight: '90%',
        }}
      >
        {chatMessages.length === 0 && <Overview />}
        {/* Messages container */}
        {chatMessages.length > 0 && (
          <Box
            ref={messagesContainerRef}
            sx={{
              flex: 1,
              width: '100%',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
              px: '72px',
              // Center content when no messages
              ...(chatMessages.length === 0 && {
                justifyContent: 'center',
                alignItems: 'center',
              }),
              // Add shadow at top when scrolled and has overflow
              ...(showScrollShadow && {
                boxShadow: 'inset 0px 4px 20px rgba(0, 0, 0, 0.02)',
              }),
              '&::-webkit-scrollbar': {
                width: 6,
              },
              '&::-webkit-scrollbar-track': {
                bgcolor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.2)'
                    : 'rgba(0,0,0,0.2)',
                borderRadius: 3,
              },
            }}
          >
            {chatMessages.map((message, index) => (
              <Grow
                key={index}
                in
                timeout={600}
                style={{ transformOrigin: '0 0 0' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    flexDirection:
                      message.role === 'user' ? 'row-reverse' : 'row',
                    justifyContent: 'flex-start',
                    ...(index === 0 && { pt: 2 }),
                  }}
                >
                  {/* Avatar */}
                  {message.role === 'assistant' ? (
                    <img
                      src={botIcon}
                      alt="ScreenApp"
                      style={{
                        width: 24,
                        height: 24,
                        objectFit: 'contain',
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <>
                      {user?.picture ? (
                        <img
                          src={user.picture}
                          alt={user?.name || 'User'}
                          style={{
                            width: 24,
                            height: 24,
                            objectFit: 'cover',
                            borderRadius: '5px',
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '5px',
                            bgcolor: (theme) =>
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.08)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <User
                            sx={{
                              width: 14,
                              height: 14,
                              color: (theme) =>
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255, 255, 255, 0.6)'
                                  : 'rgba(0, 0, 0, 0.6)',
                            }}
                          />
                        </Box>
                      )}
                    </>
                  )}

                  {/* Message content */}
                  <Box sx={{ minWidth: 0 }}>
                    {/* Show thinking animation if assistant message is empty (waiting for streaming to start) */}
                    {message.role === 'assistant' &&
                    message.content === '' &&
                    isStreaming ? (
                      <Box
                        sx={{
                          pt: 1,
                          pb: 2,
                          pl: 2,
                          pr: '10%',
                          ml: 1,
                          mt: -1,
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            display: 'inline-block',
                            '&::before, &::after, &': {
                              borderRadius: '50%',
                              width: '1.5em',
                              height: '1.5em',
                              animationFillMode: 'both',
                              animation:
                                'bblFadInOut 1.8s infinite ease-in-out',
                            },
                            color: (theme) =>
                              theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.6)'
                                : 'rgba(0,0,0,0.4)',
                            fontSize: '5px',
                            textIndent: '-9999em',
                            transform: 'translateZ(0)',
                            animationDelay: '-0.16s',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: '-2.2em',
                              animationDelay: '-0.32s',
                            },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: '2.2em',
                            },
                            '@keyframes bblFadInOut': {
                              '0%, 80%, 100%': {
                                boxShadow: (theme) =>
                                  theme.palette.mode === 'dark'
                                    ? '0 1.5em 0 -0.75em rgba(255,255,255,0.6)'
                                    : '0 1.5em 0 -0.75em rgba(0,0,0,0.4)',
                              },
                              '40%': {
                                boxShadow: (theme) =>
                                  theme.palette.mode === 'dark'
                                    ? '0 1.5em 0 0 rgba(255,255,255,0.6)'
                                    : '0 1.5em 0 0 rgba(0,0,0,0.4)',
                              },
                            },
                          }}
                        />
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          color: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'rgba(255,255,255,0.9)'
                              : 'rgba(0,0,0,0.9)',
                          lineHeight: 1.6,
                          whiteSpace: 'pre-wrap',
                          backgroundColor:
                            message.role === 'assistant'
                              ? 'transparent'
                              : (theme) =>
                                  theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : '#F9FAFB',
                          borderRadius:
                            message.role === 'assistant' ? 0 : '16px',
                          borderTopRightRadius: 0,
                          pt: message.role === 'user' ? 2 : 1,
                          pb: 2,
                          px: 2,
                          pr: message.role === 'assistant' ? '10%' : 2,
                          wordBreak: 'break-word',
                          '& strong': {
                            fontWeight: 600,
                            color: (theme) =>
                              theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,1)'
                                : 'rgba(0,0,0,1)',
                          },
                          '& div': {
                            display: 'block',
                          },
                          // Ensure proper spacing for injected divs
                          '& div[style*="margin-bottom"]': {
                            height: 0,
                            overflow: 'hidden',
                          },
                          // Style for bullet point lists
                          '& ul': {
                            margin: '12px 0',
                            paddingLeft: '20px',
                          },
                          '& li': {
                            marginBottom: '8px',
                            listStyleType: 'disc',
                            lineHeight: 1.6,
                          },
                          // Style for paragraphs
                          '& p': {
                            margin: '8px 0',
                            lineHeight: 1.6,
                          },
                          '& p:first-of-type': {
                            marginTop: 0,
                          },
                          '& p:last-of-type': {
                            marginBottom: 0,
                          },
                        }}
                        dangerouslySetInnerHTML={{
                          __html: formatMessageContent(message.content),
                        }}
                      />
                    )}

                    {/* Action buttons */}
                    {message.actionButtons && message.actionButtons.length > 0 && (
                      <Box
                        sx={{
                          mt: 2,
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          ml: 2,
                        }}
                      >
                        {message.actionButtons.map((button: ActionButton) => (
                          <DynamicActionButton
                            key={button.id}
                            button={button}
                            isLoading={isActionButtonLoading === button.id}
                            onClick={handleActionButtonClick}
                          />
                        ))}
                      </Box>
                    )}

                    {/* Feedback buttons for assistant messages */}
                    {message.role === 'assistant' && message.content && (
                      <Box
                        sx={{
                          mt: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          justifyContent: 'flex-end',
                          pr: '10%',
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleFeedback(message.id || 'test-id', 'like')
                          }
                          disabled={
                            messageFeedback[message.id || 'test-id']?.loading
                          }
                          sx={{
                            width: 28,
                            height: 28,
                            color:
                              message.feedback?.type === 'like' ||
                              messageFeedback[message.id || 'test-id']?.type ===
                                'like'
                                ? '#1976d2' // Darker blue color
                                : (theme) =>
                                    theme.palette.mode === 'dark'
                                      ? 'rgba(255,255,255,0.4)'
                                      : 'rgba(0,0,0,0.4)',
                            '&:hover': {
                              bgcolor: (theme) =>
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255,255,255,0.05)'
                                  : 'rgba(0,0,0,0.05)',
                              color: '#1976d2',
                            },
                            '&:active': {
                              bgcolor: (theme) =>
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255,255,255,0.1)'
                                  : 'rgba(0,0,0,0.1)',
                              transform: 'scale(0.95)',
                            },
                            transition: 'all 0.1s ease',
                            '&:disabled': {
                              color: (theme) =>
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255,255,255,0.2)'
                                  : 'rgba(0,0,0,0.2)',
                            },
                          }}
                        >
                          {messageFeedback[message.id || 'test-id']?.loading &&
                          messageFeedback[message.id || 'test-id']?.type ===
                            'like' ? (
                            <CircularProgress size={14} />
                          ) : (
                            <ThumbsUp
                              sx={{
                                width: 14,
                                height: 14,
                                color: 'unset',
                              }}
                            />
                          )}
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={() =>
                            handleFeedback(message.id || 'test-id', 'dislike')
                          }
                          disabled={
                            messageFeedback[message.id || 'test-id']?.loading
                          }
                          sx={{
                            width: 28,
                            height: 28,
                            color:
                              message.feedback?.type === 'dislike' ||
                              messageFeedback[message.id || 'test-id']?.type ===
                                'dislike'
                                ? '#f44336' // Red color
                                : (theme) =>
                                    theme.palette.mode === 'dark'
                                      ? 'rgba(255,255,255,0.4)'
                                      : 'rgba(0,0,0,0.4)',
                            '&:hover': {
                              bgcolor: (theme) =>
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255,255,255,0.05)'
                                  : 'rgba(0,0,0,0.05)',
                              color: '#f44336',
                            },
                            '&:active': {
                              bgcolor: (theme) =>
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255,255,255,0.1)'
                                  : 'rgba(0,0,0,0.1)',
                              transform: 'scale(0.95)',
                            },
                            transition: 'all 0.1s ease',
                            '&:disabled': {
                              color: (theme) =>
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255,255,255,0.2)'
                                  : 'rgba(0,0,0,0.2)',
                            },
                          }}
                        >
                          {messageFeedback[message.id || 'test-id']?.loading &&
                          messageFeedback[message.id || 'test-id']?.type ===
                            'dislike' ? (
                            <CircularProgress size={14} />
                          ) : (
                            <ThumbsDown
                              sx={{
                                width: 14,
                                height: 14,
                                color: 'unset',
                              }}
                            />
                          )}
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grow>
            ))}

            {/* Error message */}
            {chatError && (
              <Fade in>
                <Alert severity="error" sx={{ maxWidth: 500, mx: 'auto' }}>
                  {chatError}
                </Alert>
              </Fade>
            )}

            <div ref={messagesEndRef} />
          </Box>
        )}

        {chatMessages.length === 0 && (
          <Box
            sx={{
              width: '100%',
              marginBottom: '32px',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: '1fr 1fr',
                  lg: 'repeat(2, 1fr)',
                },
                gap: { xs: 2, md: 3 },
                px: '72px',
                mx: 'auto',
              }}
            >
              {quickActionChips.map((chip, index) => {
                // Use different timing pattern to avoid long delays on last tiles
                const delays = [0, 100, 200, 150, 250, 300];
                return (
                  <Grow
                    key={index}
                    in
                    timeout={1000}
                    style={{ transitionDelay: `${delays[index]}ms` }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        px: { xs: 3, md: 3 },
                        py: 3,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        bgcolor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.02)'
                            : '#F9FAFB',
                        border: 'none',
                        borderRadius: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        '&:hover': {
                          bgcolor: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.05)'
                              : '#E1E3E8',
                        },
                      }}
                      onClick={() => handleQuickAction(chip)}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{
                          color: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.85)'
                              : '#585858',
                          lineHeight: 1.2,
                          mb: 1,
                        }}
                      >
                        {chip.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.65)'
                              : '#626262',
                          lineHeight: 1.2,
                          fontWeight: 400,
                        }}
                      >
                        {chip.label}
                      </Typography>
                    </Paper>
                  </Grow>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Input area */}
        <Box
          sx={{
            width: '100%',
            position: 'relative',
            pl: '72px',
            pr: chatMessages.length === 0 ? '72px' : '84px',
          }}
        >
          {/* Input field */}
          <Box sx={{ position: 'relative' }}>
            <TextField
              ref={textareaRef}
              fullWidth
              multiline
              minRows={1}
              maxRows={4}
              placeholder={
                chatMessages.length > 0
                  ? 'How can I help?'
                  : 'Ask me anything about ScreenApp...'
              }
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={handleChatKeyPress}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '24px !important',
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : '#F9FAFB',
                  border: 'none',
                  padding: '10px 14px',
                  pr: 8,
                  '& fieldset': {
                    border: 'none',
                    borderRadius: '24px !important',
                  },
                  '&:hover fieldset': {
                    border: 'none',
                    borderRadius: '24px !important',
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none',
                    borderRadius: '24px !important',
                  },
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.95rem',
                  lineHeight: 1.5,
                  color: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.7)'
                      : '#7A7A7A',
                  fontWeight: 400,
                  display: 'flex',
                  alignItems: 'center',
                  boxSizing: 'border-box',
                  // pt: '26px',
                  '&::placeholder': {
                    color: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.5)'
                        : '#7A7A7A',
                    opacity: 1,
                  },
                },
              }}
            />

            {/* Send button */}
            <IconButton
              onClick={() => handleChatSend()}
              disabled={!chatInput.trim() || isStreaming}
              sx={{
                position: 'absolute',
                right: 8,
                bottom: 8,
                width: 32,
                height: 32,
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : '#fff',
                color: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.8)'
                    : 'rgba(0, 0, 0, 0.6)',
                borderRadius: '30px',
                '&:hover': {
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.15)'
                      : 'rgba(0, 0, 0, 0.03)',
                },
                '&:disabled': {
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.1)'
                      : 'rgb(255, 255, 255)',
                  color: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.3)'
                      : 'rgba(0,0,0,0.3)',
                },
              }}
            >
              <ArrowUp
                sx={{
                  width: 16,
                  height: 16,
                  color: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.8)'
                      : 'rgba(0, 0, 0, 0.6)',
                }}
              />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AIAssistantForm;
